<template>
	<div class="voltage-regulator-create">
		<section class="hero-panel">
			<div class="hero-panel__glow hero-panel__glow--left"></div>
			<div class="hero-panel__glow hero-panel__glow--right"></div>

			<div class="page-header">
				<i class="el-icon-back back-icon" @click="handleBack"></i>
				<div class="page-header__title">
					<h1 class="page-title">{{ pageTitle }}</h1>
					<p class="page-subtitle">维护调压器档案、技术参数与地图定位信息</p>
				</div>
			</div>
		</section>

		<div class="form-container" v-loading="detailLoading">
			<el-form :model="formData" :rules="formRules" ref="formRef" label-position="top">
				<!-- 基本信息 -->
				<section class="form-section">
					<div class="section-header">
						<span class="section-dot"></span>
						<span class="section-title">基本信息</span>
					</div>

					<div class="form-grid">
						<el-form-item label="调压器编号" prop="regulatorCode">
							<el-input v-model="formData.regulatorCode" placeholder="请输入调压器编号"></el-input>
						</el-form-item>

						<el-form-item label="调压柜/箱编号" prop="cabinetNo">
							<el-input
								v-model="formData.cabinetNo"
								placeholder="请输入调压柜/箱编号"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="品牌" prop="brand">
							<el-input v-model="formData.brand" placeholder="请输入品牌" clearable></el-input>
						</el-form-item>

						<el-form-item label="型号" prop="model">
							<el-input v-model="formData.model" placeholder="请输入设备型号" clearable></el-input>
						</el-form-item>

						<el-form-item label="安装地址" prop="installAddress">
							<div class="address-group">
								<el-input
									v-model="formData.installAddress"
									placeholder="请输入或通过地图拾取位置"
								></el-input>
								<el-button class="map-btn" icon="el-icon-location" @click="handleMapPick"></el-button>
							</div>
						</el-form-item>

						<el-form-item label="地理坐标" prop="coordinates">
							<div class="coordinate-group">
								<div class="coordinate-inputs">
									<el-input v-model="formData.longitude" placeholder="经度" disabled></el-input>
									<span class="coordinate-separator"></span>
									<el-input v-model="formData.latitude" placeholder="纬度" disabled></el-input>
								</div>
							</div>
						</el-form-item>

						<el-form-item label="安装日期" prop="installDate">
							<el-date-picker
								v-model="formData.installDate"
								type="date"
								placeholder="请选择安装日期"
								value-format="yyyy-MM-dd"
								style="width: 100%"
							></el-date-picker>
						</el-form-item>

						<el-form-item label="放散压力(MPa)" prop="reliefPressure">
							<el-input
								v-model="formData.reliefPressure"
								placeholder="请输入放散压力值"
								clearable
							></el-input>
						</el-form-item>
					</div>
				</section>

				<section class="form-section">
					<div class="section-header">
						<span class="section-dot"></span>
						<span class="section-title">技术参数</span>
					</div>

					<div class="form-grid">
						<el-form-item label="出厂稳压精度" prop="pressureAccuracy">
							<el-input
								v-model="formData.pressureAccuracy"
								placeholder="请输入稳压精度"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="出厂关闭等级" prop="shutdownLevel">
							<el-input
								v-model="formData.shutdownLevel"
								placeholder="请输入出厂关闭等级"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="切断压力(MPa)" prop="shutdownPressure">
							<el-input
								v-model="formData.shutdownPressure"
								placeholder="请输入切断压力值"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="额定进口压力(MPa)" prop="inletPressure">
							<el-input
								v-model="formData.inletPressure"
								placeholder="请输入额定进口压力"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="额定出口压力(MPa)" prop="outletPressure">
							<el-input
								v-model="formData.outletPressure"
								placeholder="请输入额定出口压力"
								clearable
							></el-input>
						</el-form-item>

						<el-form-item label="公称通径" prop="nominalDiameter">
							<el-input
								v-model="formData.nominalDiameter"
								placeholder="请输入公称通径"
								clearable
							></el-input>
						</el-form-item>
					</div>
				</section>

				<section class="form-section">
					<div class="section-header">
						<span class="section-dot"></span>
						<span class="section-title">备注信息</span>
					</div>

					<el-form-item label="备注" prop="remark">
						<el-input
							v-model="formData.remark"
							type="textarea"
							:rows="4"
							placeholder="请输入备注信息（选填）"
						></el-input>
					</el-form-item>
				</section>
			</el-form>
		</div>

		<div class="footer-buttons">
			<el-button class="cancel-btn" @click="handleCancel">取消</el-button>
			<el-button class="save-btn" type="primary" :loading="saveLoading" @click="handleSave">保存</el-button>
		</div>

		<gc-map-picker
			:visible.sync="mapVisible"
			:default-lng="formData.longitude"
			:default-lat="formData.latitude"
			@confirm="handleMapConfirm"
		/>
	</div>
</template>

<script>
import {
	apiAddPressureRegulator,
	apiGetPressureRegulatorDetail,
	apiUpdatePressureRegulator,
} from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'VoltageRegulatorEdit',
	data() {
		return {
			formData: {
				regulatorCode: '',
				brand: '',
				model: '',
				cabinetNo: '',
				installAddress: '',
				installDate: '',
				longitude: '',
				latitude: '',
				reliefPressure: '',
				pressureAccuracy: '',
				shutdownLevel: '',
				shutdownPressure: '',
				inletPressure: '',
				outletPressure: '',
				nominalDiameter: '',
				remark: '',
			},
			formRules: {
				regulatorCode: [{ required: true, message: '请输入调压器编号', trigger: 'blur' }],
				brand: [{ required: true, message: '请输入品牌', trigger: 'blur' }],
				model: [{ required: true, message: '请输入型号', trigger: 'blur' }],
				cabinetNo: [{ required: true, message: '请输入调压柜/箱编号', trigger: 'blur' }],
				installAddress: [{ required: true, message: '请输入详细安装地址', trigger: 'blur' }],
			},
			mapVisible: false,
			detailLoading: false,
			saveLoading: false,
		}
	},
	created() {
		if (this.isEdit) {
			this.getDetail()
		}
	},
	methods: {
		handleBack() {
			this.$router.back()
		},
		handleCancel() {
			this.$router.back()
		},
		handleSave() {
			this.$refs.formRef.validate(valid => {
				if (!valid) return
				this.submitForm()
			})
		},
		async getDetail() {
			this.detailLoading = true
			try {
				const data = await apiGetPressureRegulatorDetail(this.regulatorId)
				const paramJson = data.paramJson || {}
				this.formData = {
					...this.formData,
					regulatorCode: data.regulatorCode || '',
					brand: data.brand || '',
					model: data.model || '',
					cabinetNo: data.cabinetNo || '',
					installAddress: data.installAddress || '',
					installDate: data.installDate || '',
					longitude: data.longitude ?? '',
					latitude: data.latitude ?? '',
					reliefPressure: paramJson.reliefPressure || '',
					pressureAccuracy: paramJson.pressureAccuracy || '',
					shutdownLevel: paramJson.shutdownLevel || '',
					shutdownPressure: paramJson.shutdownPressure || '',
					inletPressure: paramJson.inletPressure || '',
					outletPressure: paramJson.outletPressure || '',
					nominalDiameter: paramJson.nominalDiameter || '',
					remark: data.remark || '',
				}
			} finally {
				this.detailLoading = false
			}
		},
		async submitForm() {
			this.saveLoading = true
			try {
				const data = this.getSubmitData()
				if (this.isEdit) {
					await apiUpdatePressureRegulator(this.regulatorId, data)
				} else {
					await apiAddPressureRegulator(data)
				}
				this.$message.success('保存成功')
				this.$store.dispatch('account/changeStatus', true)
				this.$router.push({ name: 'voltageAccount' })
			} finally {
				this.saveLoading = false
			}
		},
		getSubmitData() {
			return {
				regulatorCode: this.formData.regulatorCode,
				brand: this.formData.brand,
				model: this.formData.model,
				cabinetNo: this.formData.cabinetNo,
				installAddress: this.formData.installAddress,
				installDate: this.formData.installDate || null,
				longitude: this.normalizeNullableNumber(this.formData.longitude),
				latitude: this.normalizeNullableNumber(this.formData.latitude),
				paramJson: {
					reliefPressure: this.formData.reliefPressure,
					pressureAccuracy: this.formData.pressureAccuracy,
					shutdownLevel: this.formData.shutdownLevel,
					shutdownPressure: this.formData.shutdownPressure,
					inletPressure: this.formData.inletPressure,
					outletPressure: this.formData.outletPressure,
					nominalDiameter: this.formData.nominalDiameter,
				},
				remark: this.formData.remark,
			}
		},
		normalizeNullableNumber(value) {
			return value === '' || value === null || value === undefined ? null : Number(value)
		},
		handleMapPick() {
			this.mapVisible = true
		},
		handleMapConfirm(data) {
			this.formData.installAddress = data.address || ''
			this.formData.longitude = data.longitude
			this.formData.latitude = data.latitude
			this.$nextTick(() => {
				if (this.$refs.formRef) {
					this.$refs.formRef.validateField('installAddress')
				}
			})
		},
	},
	computed: {
		regulatorId() {
			return this.$route.params.id
		},
		isEdit() {
			return Boolean(this.regulatorId)
		},
		pageTitle() {
			return this.isEdit ? '调压器编辑' : '新增调压器'
		},
	},
}
</script>

<style lang="scss" scoped>
.voltage-regulator-create {
	--page-bg: #eef4f8;
	--panel-bg: rgba(255, 255, 255, 0.96);
	--panel-border: #dbe5ee;
	--panel-shadow: 0 18px 38px rgba(15, 23, 42, 0.05);
	--title: #0f172a;
	--text: #334155;
	--muted: #64748b;
	--line: #e2e8f0;
	--primary: #2563eb;
	--primary-soft: #eff6ff;

	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 12px;
	background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
		radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 20%),
		linear-gradient(180deg, #f5f9fc 0%, var(--page-bg) 100%);
	font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	color: var(--text);
}

.hero-panel {
	position: relative;
	overflow: hidden;
	padding: 24px 28px 22px;
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	backdrop-filter: blur(12px);
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 255, 0.94)), var(--panel-bg);
	flex-shrink: 0;
}

.hero-panel__glow {
	position: absolute;
	width: 280px;
	height: 280px;
	border-radius: 50%;
	filter: blur(18px);
	opacity: 0.25;
	pointer-events: none;
}

.hero-panel__glow--left {
	left: -120px;
	top: -120px;
	background: rgba(37, 99, 235, 0.22);
}

.hero-panel__glow--right {
	right: -100px;
	top: -120px;
	background: rgba(14, 165, 233, 0.22);
}

.page-header {
	position: relative;
	display: flex;
	align-items: center;
	gap: 16px;

	.back-icon {
		font-size: 16px;
		color: #334155;
		cursor: pointer;
		transition: all 0.2s ease;
		background: rgba(255, 255, 255, 0.92);
		border-radius: 50%;
		border: 1px solid #d7e1ea;
		width: 42px;
		height: 42px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		&:hover {
			color: var(--primary);
			border-color: #bfd4ff;
			background: #f8fbff;
		}
	}
}

.page-header__title {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.page-title {
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	letter-spacing: 0.01em;
	color: var(--title);
}

.page-subtitle {
	margin: 0;
	font-size: 14px;
	line-height: 1.6;
	color: var(--muted);
}

.form-container {
	flex: 1;
	height: 0;
	overflow-y: auto;
	padding-right: 2px;
}

.form-container ::v-deep .el-form {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.form-section {
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	backdrop-filter: blur(12px);
	padding: 22px 24px 24px;
	flex-shrink: 0;
}

.section-header {
	display: flex;
	align-items: center;
	margin-bottom: 12px;
	padding-bottom: 12px;
	border-bottom: 1px solid var(--line);

	.section-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: linear-gradient(135deg, #2563eb, #38bdf8);
		margin-right: 8px;
		box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
	}

	.section-title {
		font-size: 17px;
		font-weight: 700;
		color: var(--title);
	}
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 0 24px;

	.form-tip {
		grid-column: 1 / -1;
		font-size: 12px;
		color: #909399;
		margin-top: -12px;
		margin-bottom: 8px;
		padding-left: 0;
	}
}

.address-group {
	display: flex;
	gap: 8px;
	width: 100%;
}

.coordinate-group {
	display: flex;
	width: 100%;

	.coordinate-inputs {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.coordinate-separator {
		width: 1px;
		height: 20px;
		background-color: #dbe5ee;
	}

	.map-btn {
		width: 40px;
		padding: 0;
		flex-shrink: 0;
		height: 40px;
		border-radius: 10px;
		border-color: #d7dee8;
		background: #fff;
		color: #475569;
	}
}

.footer-buttons {
	display: flex;
	justify-content: center;
	gap: 12px;
	padding: 12px 24px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	backdrop-filter: blur(12px);
	flex-shrink: 0;

	.cancel-btn {
		width: 120px;
		margin: 0;
		height: 38px;
		border-radius: 10px;
		border-color: #d7dee8;
		color: #475569;
	}

	.save-btn {
		width: 120px;
		margin: 0;
		height: 38px;
		border-radius: 10px;
	}
}

::v-deep .el-form-item {
	margin-bottom: 12px;
}

::v-deep .el-form-item__label {
	color: var(--muted);
	font-weight: 400;
	padding-bottom: 6px;
}

::v-deep .el-input__inner,
::v-deep .el-textarea__inner,
::v-deep .el-date-editor .el-input__inner,
::v-deep .el-select .el-input__inner {
	height: 40px;
	border-radius: 10px;
	border-color: #d7dee8;
	color: var(--text);
	background: #fff;
}

::v-deep .el-textarea__inner {
	min-height: 104px !important;
	border-radius: 10px;
}

::v-deep .el-input__inner:focus,
::v-deep .el-textarea__inner:focus {
	border-color: #bfdbfe;
}

::v-deep .el-date-editor.el-input,
::v-deep .el-date-editor.el-input__inner,
::v-deep .el-select {
	width: 100%;
}

::v-deep .el-button--primary {
	background: var(--primary-soft);
	border-color: #bfdbfe;
	color: var(--primary);
}

::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus {
	background: #dbeafe;
	border-color: #93c5fd;
	color: #1d4ed8;
}

@media (max-width: 1200px) {
	.form-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 900px) {
	.page-header {
		align-items: flex-start;
	}

	.form-grid {
		grid-template-columns: 1fr;
		gap: 0;
	}

	.footer-buttons {
		padding: 12px;
	}
}
</style>
